/**
 * TransitionEngine.js
 * Generates procedural transitions (Drum Fills, Melodic Runs, Swells, Stop-Time).
 * Formerly FillGenerator.js.
 */

import { ABSTRACT_ROLES } from '../RoleAllocator.js';
import { SCALE_INTERVALS, NOTES, normalizeKey } from '../../data/theoryConstants.js';

// Mapping for General MIDI / Standard Kit
const DRUMS = {
    KICK: 36,
    SNARE: 38,
    TOM_LOW: 41,
    TOM_MID: 45,
    TOM_HIGH: 48,
    CRASH: 49,
    RIDE: 51,
    HAT_CLOSED: 42,
    HAT_OPEN: 46
};

/**
 * Generates a drum fill.
 * @param {number} barStart - Start time of the fill bar.
 * @param {string} intensity - 'low', 'medium', 'high', 'extreme'
 * @param {Object} context - Generation context containing random, config, etc.
 * @returns {Array} - Array of note events.
 */
export const generateDrumFill = (barStart, intensity = 'medium', context = {}) => {
    const events = [];
    // Robust fallback for random
    const random = context.random || {
        bool: (chance = 0.5) => Math.random() < chance,
        next: () => Math.random()
    };

    // Normalize intensity if string (legacy)
    let scalarIntensity = 0.5;
    if (intensity === 'extreme') scalarIntensity = 1.0;
    else if (intensity === 'high') scalarIntensity = 0.8;
    else if (intensity === 'medium') scalarIntensity = 0.5;
    else if (intensity === 'low') scalarIntensity = 0.2;
    else if (typeof intensity === 'number') scalarIntensity = intensity;

    // Dynamic Fill Duration (Beats)
    // Low: 1 beat, High: 4 beats
    // Quantize to nearest beat
    const fillBeats = Math.max(1, Math.round(scalarIntensity * 4));
    const fillStart = barStart + (4 - fillBeats); // Assumes 4/4

    // Dynamic Density (Step Size)
    // Low: 8th notes (0.5), High: 32nd notes (0.125)
    // Map 0.0-1.0 to 0.5-0.125
    let stepSize = 0.5;
    if (scalarIntensity > 0.7) stepSize = 0.125;
    else if (scalarIntensity > 0.4) stepSize = 0.25;

    const totalSteps = fillBeats / stepSize;

    for (let i = 0; i < totalSteps; i++) {
        const t = fillStart + (i * stepSize);
        const progress = i / totalSteps;

        // Note Selection Logic
        let note = DRUMS.SNARE;
        let role = ABSTRACT_ROLES.PERC_MID;

        // High intensity -> Use Toms/Kick more
        if (scalarIntensity > 0.6) {
            // Rolling effect: High -> Mid -> Low -> Snare
            const cycle = i % 4;
            if (cycle === 0) note = DRUMS.TOM_HIGH;
            if (cycle === 1) note = DRUMS.TOM_MID;
            if (cycle === 2) note = DRUMS.TOM_LOW;
            if (cycle === 3) { note = DRUMS.SNARE; role = ABSTRACT_ROLES.PERC_MID; }
            else role = ABSTRACT_ROLES.PERC_LOW; // Toms as low/mid
        } else {
            // Simple Snare/Kick
            if (i % 2 === 0) note = DRUMS.SNARE;
            else note = (random.bool(0.3) ? DRUMS.KICK : DRUMS.SNARE);
        }

        // Final hit (Crash on downbeat of NEXT bar usually, but here we prep it)
        // Accent last note
        let vel = 0.6 + (progress * 0.4); // Linear crescendo
        if (i === totalSteps - 1) vel = 1.0;

        events.push({
            s: t,
            d: note,
            len: stepSize * 0.8,
            vel: vel,
            role: role
        });
    }

    return events;
};

/**
 * Generates a melodic fill (e.g., Woodwind Run, String Swell).
 * @param {number} barStart - Start time of the fill bar.
 * @param {Object} chord - Current chord context.
 * @param {Object} context - Generation context (scale, key, etc.).
 * @param {string} type - 'run_up', 'swell', 'trill'
 */
export const generateMelodicFill = (barStart, chord, context, type = 'run_up') => {
    const events = [];
    const scaleType = context.scaleType || 'major';
    const rootKey = context.rootKey || 'C';

    const intervals = SCALE_INTERVALS[scaleType] || SCALE_INTERVALS.major;
    const rootIdx = NOTES.indexOf(normalizeKey(rootKey));

    // Helper to get scale note closest to a MIDI pitch
    const getNearestScaleTone = (pitch) => {
        const pc = pitch % 12;
        // Simple check: is in scale?
        // Note: This assumes scale is built on rootKey.
        // If chord is borrowed, we might want chord tones?
        // For runs, diatonic to Key is usually safest.
        return pitch; // Placeholder for robust quantization
    };

    if (type === 'run_up') {
        // Fast ascending run in last beat or two
        // Target: Octave above root of next chord? Or just up.
        const startPitch = (chord.rootPitchClass % 12) + 60; // Middle C range
        const numNotes = 8; // 32nd notes for 1 beat
        const duration = 1.0; // Last beat
        const startTime = barStart + 3.0; // Beat 4

        for (let i = 0; i < numNotes; i++) {
             // Diatonic step up
             // Just approximate with semitones mapped to scale
             const noteIndex = i;
             // We need a robust scale walker.
             // For now, chromatic run or simple mapping.
             // Let's use the full scale map.

             // Construct scale notes for a few octaves
             const scaleNotes = [];
             for(let oct=4; oct<8; oct++) {
                 intervals.forEach(inv => scaleNotes.push(rootIdx + inv + (oct*12)));
             }

             // Find start index
             let idx = scaleNotes.findIndex(n => n >= startPitch);
             if (idx === -1) idx = 10;

             const note = scaleNotes[idx + i];

             events.push({
                 s: startTime + (i * (duration / numNotes)),
                 d: note,
                 len: (duration / numNotes) * 1.1, // Legato
                 vel: 0.6 + (i/numNotes)*0.4, // Crescendo
                 role: 'decoration',
                 articulation: 'legato'
             });
        }
    } else if (type === 'swell') {
        // Long note with crescendo (handled via CC usually, but velocity here implies it)
        // Tremolo?
        const pitch = (chord.rootPitchClass % 12) + 72; // High string
        const startTime = barStart + 2.0;
        const duration = 2.0;

        // Trill / Tremolo effect via retriggering
        const subDiv = 0.125; // 32nd notes
        const count = duration / subDiv;

        for(let i=0; i<count; i++) {
             events.push({
                 s: startTime + (i * subDiv),
                 d: pitch,
                 len: subDiv * 0.9,
                 vel: 0.3 + (Math.pow(i/count, 2) * 0.7), // Exponential swell
                 role: 'decoration',
                 articulation: 'tremolo'
             });
        }
    }

    return events;
};

/**
 * Unified Transition Generator
 */
export const generateTransition = (type, barStart, context) => {
    if (type === 'drum_fill') {
        return generateDrumFill(barStart, context.intensity || 'medium', context);
    }
    if (type === 'melodic_run') {
        return generateMelodicFill(barStart, context.chord, context, 'run_up');
    }
    if (type === 'orchestral_swell') {
        return generateMelodicFill(barStart, context.chord, context, 'swell');
    }
    return [];
};
